import React, { useState, useEffect } from "react";

import { mcpConfigService, MCPServerConfig } from "mcp/services/MCPConfigService";
import { useMCP } from "mcp/hooks/useMCP";
import { useMCPAuth } from "mcp/hooks/useMCPAuth";
import { getAuthToken } from "services/AccountServices";

export const MCPConnectionPanel: React.FC = () => {
    const {
        clearError,
        connect,
        currentServerConfig,
        disconnect,
        error,
        forceReconnect,
        isCindraConnected,
        isCindraEnabled,
        isConnecting,
        lastConnectionAttempt,
        reconnectAttempts
    } = useMCP();

    const {
        authenticatedUserEmail,
        getAuthStatus,
        hasAuthToken,
        isCindraAuthenticated,
        tokenValidated,
        validatedUser
    } = useMCPAuth();

    const [savedConfigs, setSavedConfigs] = useState<MCPServerConfig[]>([]);
    const [selectedConfigName, setSelectedConfigName] = useState<string>("");
    const [customConfig, setCustomConfig] = useState<MCPServerConfig>({
        name: "",
        url: ""
    });
    const [envString, setEnvString] = useState("{}");
    const [useCustomConfig, setUseCustomConfig] = useState(false);
    const [authStatus, setAuthStatus] = useState<{ authenticated: boolean; email?: string } | null>(null);
    const [manualCheckLoading, setManualCheckLoading] = useState(false);
    const [tokenCopied, setTokenCopied] = useState(false);

    useEffect(() => {
        if (isCindraEnabled) {
            const configs = mcpConfigService.getServerConfigs();
            setSavedConfigs(configs);
        }
    }, [isCindraEnabled]);

    // Only load auth status once when the panel first opens and when connection state changes
    useEffect(() => {
        if (!isCindraEnabled) {
            setAuthStatus(null);
            return;
        }

        // If we already have validated token data, use it instead of calling getAuthStatus
        if (tokenValidated === true && validatedUser) {
            setAuthStatus({
                authenticated: true,
                email: validatedUser.email
            });
        }
        else if (tokenValidated === false) {
            setAuthStatus({ authenticated: false });
        }
        else if (isCindraConnected && tokenValidated === null) {
            // Only call getAuthStatus if we're connected but don't have validation results yet
            const loadAuthStatus = async () => {
                try {
                    const status = await getAuthStatus();
                    setAuthStatus(status);
                }
                catch (error) {
                    console.error("[MCPConnectionPanel] Error getting auth status:", error);
                    setAuthStatus(null);
                }
            };

            void loadAuthStatus();
        }
        else if (!isCindraConnected) {
            setAuthStatus(null);
        }
    }, [isCindraEnabled, isCindraConnected, tokenValidated, validatedUser, getAuthStatus]);

    // If Cindra is disabled, show disabled state
    if (!isCindraEnabled) {
        return (
            <div className="bg-gray-50 border border-gray-300 rounded-lg shadow p-6">
                <div className="text-center space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                        <div className="h-4 w-4 bg-gray-400 rounded-full"></div>
                        <h3 className="text-lg font-semibold text-gray-700">Cindra AI Features Disabled</h3>
                    </div>
                    <p className="text-gray-600 max-w-lg mx-auto">
                        Cindra AI features are currently disabled in this environment.
                        Contact your administrator to enable these features by setting the VITE_CINDRA_ENABLED environment variable to true.
                    </p>
                    <div className="bg-gray-100 border border-gray-200 rounded-md p-4 text-left">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">What are Cindra AI Features?</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• AI-powered chat assistant</li>
                            <li>• Healthcare data analysis</li>
                            <li>• Automated reporting and insights</li>
                            <li>• Integration with clinical systems via MCP</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        }
        catch {
            return false;
        }
    };

    const handleConnectSaved = async () => {
        if (!selectedConfigName) return;

        const config = mcpConfigService.getServerConfigByName(selectedConfigName);
        if (!config) return;

        try {
            await connect(config);
        }
        catch (error) {
            console.error("Connection failed:", error);
        }
    };

    const handleConnectCustom = async () => {
        if (!customConfig.url.trim() || !isValidUrl(customConfig.url)) return;

        const serverConfig: MCPServerConfig = {
            ...customConfig,
            env: envString.trim() ? JSON.parse(envString) : {}
        };

        try {
            await connect(serverConfig);
        }
        catch (error) {
            console.error("Connection failed:", error);
        }
    };

    const handleSaveCustomConfig = () => {
        if (!customConfig.name.trim() || !customConfig.url.trim() || !isValidUrl(customConfig.url)) return;

        const configToSave: MCPServerConfig = {
            ...customConfig,
            env: envString.trim() ? JSON.parse(envString) : {},
            description: "Custom configuration"
        };

        mcpConfigService.addServerConfig(configToSave);
        setSavedConfigs(mcpConfigService.getServerConfigs());

        // Reset form
        setCustomConfig({ name: "", url: "" });
        setEnvString("{}");
        setUseCustomConfig(false);
    };

    const handleDisconnect = async () => {
        try {
            await disconnect();
        }
        catch (error) {
            console.error("Disconnect failed:", error);
        }
    };

    const handleForceReconnect = async () => {
        try {
            await forceReconnect();
        }
        catch (error) {
            console.error("Force reconnect failed:", error);
        }
    };

    const handleManualAuthCheck = async () => {
        if (manualCheckLoading) return;

        setManualCheckLoading(true);
        try {
            const status = await getAuthStatus();
            setAuthStatus(status);
        }
        catch (error) {
            console.error("[MCPConnectionPanel] Manual auth check failed:", error);
            setAuthStatus(null);
        }
        finally {
            setManualCheckLoading(false);
        }
    };

    const formatLastAttempt = (date?: Date) => {
        if (!date) return null;
        return date.toLocaleTimeString();
    };

    const handleCopyToken = async () => {
        const token = getAuthToken();
        if (token) {
            try {
                await navigator.clipboard.writeText(token);
                setTokenCopied(true);
                setTimeout(() => setTokenCopied(false), 2000);
            }
            catch (error) {
                console.error("Failed to copy token:", error);
            }
        }
    };

    return (
        <div className="bg-white border border-gray-300 rounded-lg shadow p-6">
            <div className="space-y-6">
                {/* Cindra Status */}
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">Cindra AI Enabled</h3>
                            <div className="mt-2 text-sm text-green-700">
                                Cindra AI features are available in this environment.
                            </div>
                        </div>
                    </div>
                </div>
                {/* Connection Status */}
                {
                    error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3 flex-1">
                                    <h3 className="text-sm font-medium text-red-800">Connection Error</h3>
                                    <div className="mt-2 text-sm text-red-700">{error}</div>
                                    {
                                        reconnectAttempts > 0 && (
                                            <div className="mt-2 text-sm text-red-600">
                                                Reconnection attempts: {reconnectAttempts}
                                                {
                                                    lastConnectionAttempt && (
                                                        <span className="ml-2">
                                                            (Last attempt: {formatLastAttempt(lastConnectionAttempt)})
                                                        </span>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                    <div className="mt-3 flex space-x-2">
                                        <button
                                            onClick={clearError}
                                            className="text-sm bg-red-100 text-red-800 rounded px-2 py-1 hover:bg-red-200"
                                        >
                                            Dismiss
                                        </button>
                                        {
                                            !isConnecting && (
                                                <button
                                                    onClick={handleForceReconnect}
                                                    className="text-sm bg-blue-100 text-blue-800 rounded px-2 py-1 hover:bg-blue-200"
                                                >
                                                    Retry Connection
                                                </button>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
                {/* Reconnection Status */}
                {
                    !isCindraConnected && reconnectAttempts > 0 && !error && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="animate-spin h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">Reconnecting</h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        Attempting to reconnect... (attempt #{reconnectAttempts})
                                        {
                                            lastConnectionAttempt && (
                                                <div className="mt-1">
                                                    Last attempt: {formatLastAttempt(lastConnectionAttempt)}
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
                {
                    isCindraConnected
                        ? (
                            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                                <div className="space-y-3">
                                    {/* Server name with icons */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-sm font-medium text-green-800">
                                                {currentServerConfig?.name || "MCP Server"}
                                            </span>
                                            {
                                                reconnectAttempts > 0 && (
                                                    <span className="text-xs text-green-600">
                                                        (Reconnected after {reconnectAttempts} attempts)
                                                    </span>
                                                )
                                            }
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {/* Refresh/Reconnect icon */}
                                            <button
                                                onClick={handleForceReconnect}
                                                disabled={isConnecting}
                                                className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                                title="Reconnect"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                            </button>
                                            {/* Disconnect icon */}
                                            <button
                                                onClick={handleDisconnect}
                                                className="text-red-600 hover:text-red-700"
                                                title="Disconnect"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Authentication status - embedded inside the bubble */}
                                    {
                                        isCindraConnected && (
                                            <>
                                                {
                                                    (isCindraAuthenticated || authStatus?.authenticated || (tokenValidated === true && validatedUser))
                                                        ? (
                                                            <div className="space-y-2 pl-7">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-green-700">
                                                                        Authenticated{
                                                                            authenticatedUserEmail
                                                                                ? ` as: ${authenticatedUserEmail}`
                                                                                : validatedUser?.email
                                                                                    ? ` as: ${validatedUser.email}`
                                                                                    : authStatus?.email
                                                                                        ? ` as: ${authStatus.email}`
                                                                                        : ""
                                                                        }
                                                                    </span>
                                                                    <button
                                                                        onClick={handleManualAuthCheck}
                                                                        disabled={manualCheckLoading}
                                                                        className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                                                        title="Refresh authentication"
                                                                    >
                                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                                {/* Auth token display with copy button */}
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-xs text-green-600">Token:</span>
                                                                    <code className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-mono flex-1 truncate">
                                                                        {getAuthToken()?.substring(0, 40)}...
                                                                    </code>
                                                                    <button
                                                                        onClick={handleCopyToken}
                                                                        className="text-green-600 hover:text-green-700"
                                                                        title="Copy token"
                                                                    >
                                                                        {
                                                                            tokenCopied
                                                                                ? (
                                                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                                    </svg>
                                                                                )
                                                                                : (
                                                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                                    </svg>
                                                                                )
                                                                        }
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )
                                                        : tokenValidated === false
                                                            ? (
                                                                <div className="space-y-2 pl-7">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-red-600">
                                                                            Authentication Failed
                                                                        </span>
                                                                        <button
                                                                            onClick={handleManualAuthCheck}
                                                                            disabled={manualCheckLoading}
                                                                            className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                                                            title="Refresh authentication"
                                                                        >
                                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                    <div className="text-xs text-red-600">
                                                                        Token validation failed. Please refresh your session.
                                                                    </div>
                                                                </div>
                                                            )
                                                            : (
                                                                <div className="space-y-2 pl-7">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-gray-600">
                                                                            {hasAuthToken ? "Token Available (Checking...)" : "No Authentication Token"}
                                                                        </span>
                                                                        <button
                                                                            onClick={handleManualAuthCheck}
                                                                            disabled={manualCheckLoading}
                                                                            className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                                                            title="Refresh authentication"
                                                                        >
                                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                    {
                                                                        !hasAuthToken && (
                                                                            <div className="text-xs text-red-600">
                                                                                Please ensure you are logged into the main application.
                                                                            </div>
                                                                        )
                                                                    }
                                                                </div>
                                                            )
                                                }
                                            </>
                                        )
                                    }
                                </div>
                            </div>
                        )
                        : (
                            <div className="space-y-6">
                                {/* Toggle between saved and custom configs */}
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => setUseCustomConfig(false)}
                                        className={
                                            `px-3 py-2 text-sm font-medium rounded ${
                                                !useCustomConfig
                                                    ? "bg-blue-100 text-blue-700 border-blue-300"
                                                    : "bg-gray-100 text-gray-600 border-gray-300"
                                            } border`
                                        }
                                    >
                                        Saved Configurations
                                    </button>
                                    <button
                                        onClick={() => setUseCustomConfig(true)}
                                        className={
                                            `px-3 py-2 text-sm font-medium rounded ${
                                                useCustomConfig
                                                    ? "bg-blue-100 text-blue-700 border-blue-300"
                                                    : "bg-gray-100 text-gray-600 border-gray-300"
                                            } border`
                                        }
                                    >
                                        Custom Configuration
                                    </button>
                                </div>
                                {
                                    !useCustomConfig
                                        ? (
                                        /* Saved Configurations */
                                            <div className="space-y-4">
                                                <div>
                                                    <label htmlFor="savedConfig" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Select Server Configuration
                                                    </label>
                                                    <select
                                                        id="savedConfig"
                                                        value={selectedConfigName}
                                                        onChange={(e) => setSelectedConfigName(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">Choose a server configuration...</option>
                                                        {
                                                            savedConfigs.map((config) => (
                                                                <option key={config.name} value={config.name}>
                                                                    {config.name + " - " + (config.description || config.url)}
                                                                </option>
                                                            ))
                                                        }
                                                    </select>
                                                </div>
                                                {
                                                    selectedConfigName && (
                                                        <div className="bg-gray-50 p-3 rounded-md">
                                                            {
                                                                (() => {
                                                                    const config = mcpConfigService.getServerConfigByName(selectedConfigName);
                                                                    return config
                                                                        ? (
                                                                            <div className="text-sm text-gray-600">
                                                                                <p><strong>URL:</strong> {config.url}</p>
                                                                                {
                                                                                    config.env && Object.keys(config.env).length > 0 && (
                                                                                        <p><strong>Environment:</strong> {Object.keys(config.env).join(", ")}</p>
                                                                                    )
                                                                                }
                                                                                {
                                                                                    config.description && (
                                                                                        <p><strong>Description:</strong> {config.description}</p>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        )
                                                                        : null;
                                                                })()
                                                            }
                                                        </div>
                                                    )
                                                }
                                                <button
                                                    onClick={handleConnectSaved}
                                                    disabled={isConnecting || !selectedConfigName}
                                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                >
                                                    {
                                                        isConnecting
                                                            ? (
                                                                <>
                                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                    Connecting...
                                                                </>
                                                            )
                                                            : (
                                                                "Connect"
                                                            )
                                                    }
                                                </button>
                                            </div>
                                        )
                                        : (
                                            /* Custom Configuration */
                                            <div className="space-y-4">
                                                <div>
                                                    <label htmlFor="customServerName" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Server Name
                                                    </label>
                                                    <input
                                                        id="customServerName"
                                                        type="text"
                                                        value={customConfig.name}
                                                        onChange={(e) => setCustomConfig({ ...customConfig, name: e.target.value })}
                                                        placeholder="My Custom Server"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="customUrl" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Server URL *
                                                    </label>
                                                    <input
                                                        id="customUrl"
                                                        type="url"
                                                        value={customConfig.url}
                                                        onChange={(e) => setCustomConfig({ ...customConfig, url: e.target.value })}
                                                        placeholder="http://localhost:8001/ or https://api.example.com/mcp"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        required
                                                    />
                                                    {
                                                        customConfig.url && !isValidUrl(customConfig.url) && (
                                                            <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
                                                        )
                                                    }
                                                </div>
                                                <div>
                                                    <label htmlFor="customEnv" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Environment Variables (JSON)
                                                    </label>
                                                    <textarea
                                                        id="customEnv"
                                                        value={envString}
                                                        onChange={(e) => setEnvString(e.target.value)}
                                                        placeholder='{"API_KEY": "your-key", "DEBUG": "true"}'
                                                        rows={3}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                                                    />
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={handleConnectCustom}
                                                        disabled={isConnecting || !customConfig.url.trim() || !isValidUrl(customConfig.url)}
                                                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                    >
                                                        {
                                                            isConnecting
                                                                ? (
                                                                    <>
                                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Connecting...
                                                                    </>
                                                                )
                                                                : (
                                                                    "Connect"
                                                                )
                                                        }
                                                    </button>
                                                    <button
                                                        onClick={handleSaveCustomConfig}
                                                        disabled={!customConfig.name.trim() || !customConfig.url.trim() || !isValidUrl(customConfig.url)}
                                                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Save Config
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                }
                            </div>
                        )
                }
            </div>
        </div>
    );
};